import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { Modal } from 'javascripts/components/modal';
import { SocialSharingButton } from 'javascripts/components/social_sharing_button';
import { BookCardHorizontalCondensed } from 'javascripts/components/book_card_horizontal_condensed';

import { SocialSharing } from 'javascripts/shared/utils/social_sharing';
import styles from 'stylesheets/components/social_sharing_modal';

export function SocialSharingModal({
  audiobook,
  showFacebook,
  showTwitter,
  showPinterest,
  showEmail,
  showLink,
  isOpen,
  onRequestClose,
  socialSharingProps,
}) {
  const socialSharing = useMemo(() => {
    return new SocialSharing(socialSharingProps);
  }, []);

  function renderBookCard() {
    return (
      <div className={styles.bookCardWrapper}>
        <BookCardHorizontalCondensed audiobook={audiobook} />
      </div>
    );
  }

  function onFacebookClick() {
    SocialSharing.trackClick('Facebook');
    socialSharing.shareFacebook();
  };

  function renderFacebookButton() {
    return (
      <div className={styles.socialSharingButtonWrapper}>
        <SocialSharingButton platform='facebook' label='Facebook' onClickHandler={onFacebookClick} withLabel />
      </div>
    );
  }

  function onTwitterClick() {
    SocialSharing.trackClick('Twitter');
    socialSharing.shareTwitter();
  }

  function renderTwitterButton() {
    return (
      <div className={`${styles.socialSharingButtonWrapper}`}>
        <SocialSharingButton platform='twitter' label='Twitter' onClickHandler={onTwitterClick} withLabel />
      </div>
    );
  }

  function onPinterestClick() {
    SocialSharing.trackClick('Pinterest');
    socialSharing.sharePinterest(audiobook.coverUrl);
  }

  function renderPinterestButton() {
    return (
      <div className={`${styles.socialSharingButtonWrapper}`}>
        <SocialSharingButton platform='pinterest' label='Pinterest' onClickHandler={onPinterestClick} withLabel />
      </div>
    );
  }

  function onEmailClick() {
    SocialSharing.trackClick('Email');
  }

  function renderEmailButton() {
    const emailSubject = `Check out this audiobook from Chirp: ${audiobook.displayTitle}`;
    const emailBody = socialSharing.generateEmailBody();
    const href = `mailto:?subject=${emailSubject}&body=${emailBody}`;

    return (
      <div className={`${styles.socialSharingButtonWrapper}`}>
        <SocialSharingButton
          platform='email'
          label='Email'
          onClickHandler={onEmailClick}
          linkHref={href}
          linkTarget='_self'
          withLabel
        />
      </div>
    );
  }

  function onLinkClick() {
    SocialSharing.trackClick('Link');
  }

  function renderLinkButton() {
    return (
      <div className={`${styles.socialSharingButtonWrapper}`}>
        <SocialSharingButton platform='link' label='Copy Link' onClickHandler={onLinkClick} linkHref={socialSharing.generateShareUrl('link')} withLabel />
      </div>
    );
  }

  function renderModalContent() {
    return (
      <div className={styles.socialSharingModal} data-testid='social-sharing-modal'>
        <div className={styles.header}>Share</div>
        {renderBookCard()}
        {showLink && renderLinkButton()}
        {showFacebook && renderFacebookButton()}
        {showTwitter && renderTwitterButton()}
        {showPinterest && renderPinterestButton()}
        {showEmail && renderEmailButton()}
      </div>
    );
  }

  return (
    <Modal appSelector='#audiobook-app' isOpen={isOpen} onRequestClose={onRequestClose}>
      {renderModalContent()}
    </Modal>
  );
}

SocialSharingModal.propTypes = {
  audiobook: ChirpPropShapes.audiobook.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  showFacebook: PropTypes.bool,
  showTwitter: PropTypes.bool,
  showPinterest: PropTypes.bool,
  showEmail: PropTypes.bool,
  showLink: PropTypes.bool,
  socialSharingProps: PropTypes.object,
  twitterShareTextOverride: PropTypes.string,
  emailShareTextOverride: PropTypes.string,
};

SocialSharingModal.defaultProps = {
  isOpen: false,
  showFacebook: true,
  showTwitter: true,
  showPinterest: true,
  showEmail: true,
  showLink: true,
  socialSharingProps: {},
};
